import type { components } from "../../api/schema";

// character-unlock spec の昇格判定 helper (純粋関数)。
// hook 側で前回 result を ref 保持し、refetch のたびに本関数で diff を取る。
// UI 側は戻り値の PromotedCharacter[] を toast 入力として消費する。

export type CharacterUnlockResponse =
  components["schemas"]["UnlockedCharacterListResponse"];
export type UnlockedCharacter = CharacterUnlockResponse["unlockedCharacters"][number];

// 昇格レポート 1 件。
// fromLevel は prev.entry?.level ?? 0 (= prev に存在しないキャラは未発見扱い)、
// toLevel は next.entry.level (= 1|2|3、level 0 のキャラは next に entry が無いため対象外)。
export type PromotedCharacter = {
  characterId: string;
  name: string;
  fromLevel: 0 | 1 | 2 | 3;
  toLevel: 1 | 2 | 3;
};

// prev === undefined は「初回 fetch」を意味する。Risk #3 (デザイン)
// 初回ロードで全キャラを toast 通知すると煩雑なため、明示的に空配列で抑制する。
export function diffLevels(
  prev: CharacterUnlockResponse | undefined,
  next: CharacterUnlockResponse,
): PromotedCharacter[] {
  if (prev === undefined) return [];

  const prevByCharId = new Map<string, UnlockedCharacter>();
  for (const entry of prev.unlockedCharacters) {
    prevByCharId.set(entry.characterId, entry);
  }

  const promoted: PromotedCharacter[] = [];
  for (const nextEntry of next.unlockedCharacters) {
    const prevEntry = prevByCharId.get(nextEntry.characterId);
    // prev に entry が無いキャラは level 0 (未発見) からの昇格扱い。
    const fromLevel: 0 | 1 | 2 | 3 = prevEntry?.level ?? 0;
    if (nextEntry.level > fromLevel) {
      promoted.push({
        characterId: nextEntry.characterId,
        name: nextEntry.name,
        fromLevel,
        toLevel: nextEntry.level,
      });
    }
  }
  return promoted;
}
